import { FC, useContext } from 'react';
import { Space, Button, Grid } from 'antd';
import { useSnapshot } from 'valtio';
import FlexBox from '../atoms/FlexBox';
import Icon from '../atoms/Icon';

import { formStore } from '../../store';
import { SidebarControlContext } from '../../providers/SidebarControlContext';

import FormAssignControl from './FormAssignControl';
import FormNumberLabel from './form/FormNumberLabel';
import FormTypeLabel from './form/FormTypeLabel';
import FormSubscriberControl from './form/FormSubscriberControl';
import FormVisibilityModeSelect from './form/FormVisibilityModeSelect';
import FormVersionSelect from './form/FormVersionSelect';
import FormWorkflowSelect from './form/FormWorkflowSelect';
import FormSplitControl from './form/FormSplitControl';
import FormAccessButton from './form/FormAccessButton';
import FormUndoButtons from './form/FormUndoButtons';
import { FormRemindersButton } from './FormReminders/FormRemindersButton';
import { FormTimelineButton } from './FormTimelineButton';
import { DesktopKeepControl } from '../desktopSync';
import { useFileSyncAccess } from '../../use/desktopSync/useFileSyncAccess';

/**
 * Properties for the FormControl component
 */
interface FormControlProps {
  /** State for sidebar open/close */
  isSidebarOpen: boolean;
  /** Function to toggle sidebar open/close */
  setIsSidebarOpen: (value: boolean) => void;
}

/**
 * Main control panel for form operations and settings.
 *
 * This component provides a comprehensive toolbar for form management, including:
 * - Form number and form type, leading the row and kept on screen by the
 *   sticky bar the toolbar sits in
 * - Form version selection and access control display
 * - Form splitting and filtering controls
 * - Visibility mode selection for different display options
 * - Form assignment and subscriber management
 * - Workflow selection and execution
 * - Access permissions modal for security management
 * - Undo and redo over the form's recorded edits, repeated in `ArTableControls`
 *   because a full-screen table hides this bar
 * - Desktop sync: `DesktopKeepControl` pins `forms/{formId}` — the form, not the
 *   open version, because the agent materializes one folder per form. It stays
 *   enabled on a read-only form: the pin is on the reader's own user document.
 *   It is absent, not disabled, without the `fileSync` right — Syncretic File
 *   Sync is unreleased, so a disabled button would advertise a capability nobody
 *   can obtain. Gating here is also what keeps its pin listener unopened.
 * - Reminders and Timeline buttons, side by side, each opening the sidebar on
 *   its own view
 * - Sidebar toggle for timeline and entity views. Closing through this toggle
 *   bypasses `closeSidebar`, so it drops the just-created-record mark itself —
 *   otherwise the record would mount unlocked again on the next open.
 *
 * The component is organized into two main sections: left-side controls for
 * version and access management, and right-side controls for form operations
 * and workflows. Both are toolbars, "Form controls" (the whole bar) and "Form
 * actions" (the right group). Below `md` the bar and the right group wrap rather
 * than collapse into a menu: every action is a stateful control with its own
 * popover, modal or sidebar wiring. `md === false` is the narrow test, so the
 * empty first-render breakpoint map keeps the desktop layout.
 *
 * @param props - The component properties
 * @returns JSX element representing the form control panel
 */
const FormControl: FC<FormControlProps> = ({
  isSidebarOpen,
  setIsSidebarOpen,
}: FormControlProps) => {
  const snap = useSnapshot(formStore.state);
  const formId = snap.formId ?? '';
  const sidebarControl = useContext(SidebarControlContext);
  const hasFileSyncAccess = useFileSyncAccess();
  const { md } = Grid.useBreakpoint();
  const narrow = md === false;

  return (
    <FlexBox
      alignStart
      style={{
        marginTop: 10,
        marginBottom: 10,
      }}
      stretch
      wrap={narrow}
      role="toolbar"
      aria-label="Form controls"
    >
      <FlexBox noGrow wrap gap={5} justifyStart alignStart>
        <FormNumberLabel />
        <FormTypeLabel />
        <FormVersionSelect />
        <FormAccessButton />
        <Space.Compact>
          <FormVisibilityModeSelect />
          <FormSplitControl />
        </Space.Compact>
        <FormSubscriberControl />
        <FormAssignControl />
      </FlexBox>
      <FlexBox noGrow wrap={narrow} gap={5} role="toolbar" aria-label="Form actions">
        <FormUndoButtons surface="form controls" />
        <FormWorkflowSelect formId={formId} />
        {hasFileSyncAccess && (
          <DesktopKeepControl
            artifactPath={snap.formId ? `forms/${snap.formId}` : undefined}
            artifactLabel="form"
          />
        )}
        <FormRemindersButton />
        <FormTimelineButton />
        <Button
          type="text"
          icon={<Icon.Sidebar size={22} shift={4} />}
          onClick={() => {
            if (isSidebarOpen) sidebarControl?.setNewRecordId(undefined);
            setIsSidebarOpen(!isSidebarOpen);
          }}
          aria-label="Toggle form sidebar"
        />
      </FlexBox>
    </FlexBox>
  );
};

export default FormControl;
